import React, { useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { types as sdkTypes } from '../../util/sdkLoader';
import { userDisplayNameAsString } from '../../util/data';
import { formatOfferUnitPrice, getOfferPriceDetails } from '../../util/offerPricing';
import { getListingQuantityValue } from '../../util/listingQuantity';
import {
  LISTING_STATE_PUBLISHED,
  LISTING_UNIT_TYPES,
  STOCK_INFINITE_ITEMS,
} from '../../util/types';
import {
  CART_UPDATED_EVENT,
  cartItemsForProtectedData,
  getCartItemKey,
  isAcceptedOfferCartItem,
  readCartItems,
  removeCartItem,
  removeCartItems,
  updateCartItemQuantity,
  writeCartItems,
} from '../../util/cart';
import { cartNoPaymentCheckout } from '../../util/api';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import { getProcess, NEGOTIATION_PROCESS_NAME } from '../../transactions/transaction';
import { makeTransitionThunk } from '../TransactionPage/TransactionPage.duck';

import {
  Button,
  H1,
  H2,
  LayoutSingleColumn,
  Modal,
  NamedLink,
  Page,
  PrimaryButton,
  SecondaryButton,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { searchCartListings } from './CartPage.duck';

import css from './CartPage.module.css';

const { Money } = sdkTypes;
const BUYER_REQUEST_UNIT_TYPE = 'request';
const NEGOTIATION_PROCESS = getProcess(NEGOTIATION_PROCESS_NAME);
const ACCEPT_OFFER_WITHOUT_PAYMENT_TRANSITION =
  NEGOTIATION_PROCESS.transitions.ACCEPT_OFFER_WITHOUT_PAYMENT;

const toPositiveInteger = value => {
  const quantity = Number.parseInt(value, 10);
  return Number.isInteger(quantity) && quantity > 0 ? quantity : null;
};

const moneyFromCartPrice = price => {
  return Number.isInteger(price?.amount) && price?.currency
    ? new Money(price.amount, price.currency)
    : null;
};

const acceptedOfferLineItem = transaction => {
  const lineItems = transaction?.attributes?.lineItems || [];
  return lineItems.find(item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal);
};

const getAcceptedOfferUnitPrice = transaction => {
  const lineItem = acceptedOfferLineItem(transaction);
  return lineItem?.unitPrice || lineItem?.lineTotal || transaction?.attributes?.payinTotal || null;
};

const getAcceptedOfferOriginalQuantity = (cartEntry, listing, transaction) => {
  return (
    toPositiveInteger(cartEntry?.offeredQuantity) ||
    toPositiveInteger(cartEntry?.offerQuantity) ||
    toPositiveInteger(cartEntry?.originalQuantity) ||
    getOfferPriceDetails({ transaction, listing }).quantity ||
    toPositiveInteger(cartEntry?.quantity) ||
    1
  );
};

const getAcceptedOfferCartPriceDetails = (cartEntry, listing, transaction) => {
  const quantity = toPositiveInteger(cartEntry?.quantity) || 1;
  const originalQuantity = getAcceptedOfferOriginalQuantity(cartEntry, listing, transaction);
  const originalDetails = getOfferPriceDetails({ transaction, listing });
  const unitPrice =
    moneyFromCartPrice(cartEntry?.price) ||
    originalDetails.unitPrice ||
    getAcceptedOfferUnitPrice(transaction);
  const originalTotal = moneyFromCartPrice(cartEntry?.lineTotal) || originalDetails.totalPrice;
  const recalculatedTotal = unitPrice
    ? new Money(unitPrice.amount * quantity, unitPrice.currency)
    : null;

  return {
    quantity,
    originalQuantity,
    unitPrice,
    totalPrice:
      originalTotal && originalQuantity === quantity
        ? originalTotal
        : recalculatedTotal || originalTotal,
  };
};

const getCartEntryPrice = (cartEntry, listing, transaction) => {
  const acceptedOfferPrice = isAcceptedOfferCartItem(cartEntry)
    ? getAcceptedOfferCartPriceDetails(cartEntry, listing, transaction).unitPrice
    : null;
  return (
    acceptedOfferPrice || moneyFromCartPrice(cartEntry?.price) || listing?.attributes?.price || null
  );
};

const getCartEntryLineTotal = (cartEntry, listing, transaction) => {
  if (isAcceptedOfferCartItem(cartEntry)) {
    return getAcceptedOfferCartPriceDetails(cartEntry, listing, transaction).totalPrice;
  }

  const price = getCartEntryPrice(cartEntry, listing, transaction);
  const quantity = cartEntry?.quantity || 1;
  return price ? new Money(price.amount * quantity, price.currency) : null;
};

const getCartEntryTitle = (cartEntry, listing) => {
  return cartEntry?.title || listing?.attributes?.title || '';
};

const getCartEntryDescription = (cartEntry, listing) => {
  return cartEntry?.description || listing?.attributes?.description || '';
};

const getCartEntrySellerDisplayName = (cartEntry, listing) => {
  return (
    cartEntry?.sellerDisplayName ||
    cartEntry?.providerDisplayName ||
    cartEntry?.otherPartyDisplayName ||
    userDisplayNameAsString(listing?.author, '')
  );
};

const listingImageUrl = listing => {
  const firstImage = listing?.images?.[0];
  const variants = firstImage?.attributes?.variants || {};
  return variants['listing-card']?.url || variants['listing-card-2x']?.url || null;
};

const cartEntryImageUrl = (cartEntry, listing) => cartEntry?.image || listingImageUrl(listing);

const getStockQuantity = listing => listing?.currentStock?.attributes?.quantity;

const getListingTypeConfig = (config, listing) => {
  const listingType = listing?.attributes?.publicData?.listingType;
  return (config?.listing?.listingTypes || []).find(conf => conf.listingType === listingType);
};

const isInfiniteStockListing = (config, listing) =>
  STOCK_INFINITE_ITEMS.includes(getListingTypeConfig(config, listing)?.stockType);

const isBuyerRequestListing = listing =>
  listing?.attributes?.publicData?.unitType === BUYER_REQUEST_UNIT_TYPE;

const hasUnlimitedCartStock = (cartEntry, listing, config) => {
  if (isAcceptedOfferCartItem(cartEntry)) {
    return cartEntry?.isInfiniteStock === true || isInfiniteStockListing(config, listing);
  }

  return (
    cartEntry?.isInfiniteStock === true ||
    isBuyerRequestListing(listing) ||
    isInfiniteStockListing(config, listing)
  );
};

const getCartStockLimit = (cartEntry, listing, config, transaction) => {
  if (hasUnlimitedCartStock(cartEntry, listing, config)) {
    return null;
  }

  const listingQuantity = isAcceptedOfferCartItem(cartEntry)
    ? getListingQuantityValue(listing, { includeStock: false })
    : null;
  if (listingQuantity) {
    return listingQuantity;
  }

  const stock = getStockQuantity(listing);
  if (Number.isInteger(stock)) {
    return stock > 0 ? stock : 0;
  }

  return isAcceptedOfferCartItem(cartEntry)
    ? getAcceptedOfferOriginalQuantity(cartEntry, listing, transaction)
    : null;
};

const isUnavailable = (cartEntry, listing, config, transaction) => {
  if (!listing) {
    return true;
  }

  const stockLimit = getCartStockLimit(cartEntry, listing, config, transaction);
  const hasInfiniteStock = hasUnlimitedCartStock(cartEntry, listing, config);
  const hasRequiredPrice =
    isBuyerRequestListing(listing) || !!getCartEntryPrice(cartEntry, listing, transaction);
  return (
    listing?.attributes?.state !== LISTING_STATE_PUBLISHED ||
    !hasRequiredPrice ||
    (!hasInfiniteStock && (stockLimit == null || stockLimit < 1))
  );
};

const getDeliveryMethod = (cartEntry, listing) => {
  const publicData = listing?.attributes?.publicData || {};
  if (cartEntry?.deliveryMethod && cartEntry.deliveryMethod !== 'none') {
    return cartEntry.deliveryMethod;
  }
  if (publicData.shippingEnabled && !publicData.pickupEnabled) {
    return 'shipping';
  }
  if (!publicData.shippingEnabled && publicData.pickupEnabled) {
    return 'pickup';
  }
  return publicData.shippingEnabled ? 'shipping' : publicData.pickupEnabled ? 'pickup' : null;
};

const toCartCheckoutItem = (listing, cartEntry, config, transaction) => {
  const publicData = listing?.attributes?.publicData || {};
  const quantity = cartEntry?.quantity || 1;
  const firstImage = listingImageUrl(listing);
  const price = getCartEntryPrice(cartEntry, listing, transaction);
  const lineTotal = getCartEntryLineTotal(cartEntry, listing, transaction);
  const cartItemIsAcceptedOffer = isAcceptedOfferCartItem(cartEntry);
  const offeredQuantity = cartItemIsAcceptedOffer
    ? getAcceptedOfferOriginalQuantity(cartEntry, listing, transaction)
    : null;
  const sellerDisplayName = getCartEntrySellerDisplayName(cartEntry, listing);
  const authorId = cartItemIsAcceptedOffer
    ? cartEntry?.otherPartyId || cartEntry?.sellerId || listing.author?.id?.uuid
    : listing.author?.id?.uuid;
  const isInfiniteStock =
    cartEntry?.isInfiniteStock === true || hasUnlimitedCartStock(cartEntry, listing, config);

  return {
    listingId: listing.id.uuid,
    title: getCartEntryTitle(cartEntry, listing),
    description: getCartEntryDescription(cartEntry, listing),
    authorId,
    sellerId: cartEntry?.sellerId || authorId,
    sellerDisplayName,
    providerId: cartEntry?.providerId || (cartItemIsAcceptedOffer ? authorId : null),
    providerDisplayName: cartEntry?.providerDisplayName || sellerDisplayName,
    customerId: cartEntry?.customerId || null,
    customerDisplayName: cartEntry?.customerDisplayName || null,
    otherPartyId: cartEntry?.otherPartyId || null,
    otherPartyDisplayName: cartEntry?.otherPartyDisplayName || null,
    price,
    lineTotal,
    currency: price?.currency,
    stockCount: quantity,
    offeredQuantity,
    deliveryMethod: getDeliveryMethod(cartEntry, listing),
    isInfiniteStock,
    selectedOptions: cartEntry?.selectedOptions || null,
    shippingPriceInSubunitsOneItem: publicData.shippingPriceInSubunitsOneItem,
    shippingPriceInSubunitsAdditionalItems: publicData.shippingPriceInSubunitsAdditionalItems,
    ...(firstImage ? { firstImage } : {}),
  };
};

const checkoutItemSubtotal = checkoutItems => {
  const currency =
    checkoutItems.find(item => item.lineTotal?.currency)?.lineTotal?.currency ||
    checkoutItems.find(item => item.price?.currency)?.price?.currency ||
    null;
  const amount = checkoutItems.reduce((sum, item) => {
    return sum + (item.lineTotal?.amount || (item.price?.amount || 0) * (item.stockCount || 1));
  }, 0);
  return currency ? { amount, currency } : null;
};

const cartProtectedDataForCheckoutItems = checkoutItems => {
  const protectedCartItems = cartItemsForProtectedData(checkoutItems);
  const subtotal = checkoutItemSubtotal(checkoutItems);
  return {
    cartCheckout: true,
    cartItemIds: checkoutItems.map(item => item.cartItemId || item.listingId),
    cartListingIds: checkoutItems.map(item => item.listingId),
    cartTransactionIds: checkoutItems.map(item => item.transactionId).filter(Boolean),
    cartItems: JSON.stringify(protectedCartItems),
    cartCheckoutItems: protectedCartItems,
    cartSellerId: checkoutItems[0]?.authorId,
    cartItemCount: checkoutItems.reduce((sum, item) => sum + (item.stockCount || 1), 0),
    ...(subtotal
      ? {
          cartSubtotal: subtotal,
          cartSubtotalAmount: subtotal.amount,
          cartSubtotalCurrency: subtotal.currency,
        }
      : {}),
  };
};

const logCartCheckoutHandoff = ({ checkoutItems, anchorTransactionId }) => {
  // eslint-disable-next-line no-console
  console.info('[cart-checkout] frontend handoff', {
    anchorTransactionId,
    cartItems: checkoutItems.map(item => ({
      cartItemId: item.cartItemId,
      listingId: item.listingId,
      transactionId: item.transactionId,
      itemType: item.itemType,
      title: item.title,
      description: item.description,
      sellerDisplayName: item.sellerDisplayName,
      providerDisplayName: item.providerDisplayName,
      quantity: item.stockCount,
      offerPrice: item.price
        ? {
            amount: item.price.amount,
            currency: item.price.currency,
          }
        : null,
      lineTotal: item.lineTotal
        ? {
            amount: item.lineTotal.amount,
            currency: item.lineTotal.currency,
          }
        : null,
    })),
    computedCartSubtotal: checkoutItemSubtotal(checkoutItems),
  });
};

const toCheckoutCartItemWithMetadata = (listing, cartEntry, config, transaction) => {
  const cartItemId = getCartItemKey(cartEntry);
  return {
    ...toCartCheckoutItem(listing, cartEntry, config, transaction),
    cartItemId,
    itemType: cartEntry.itemType,
    transactionId: cartEntry.transactionId,
  };
};

const getOtherPartyDisplayName = (cartEntry, listing) => {
  return (
    cartEntry?.otherPartyDisplayName ||
    cartEntry?.sellerDisplayName ||
    userDisplayNameAsString(listing?.author, '')
  );
};

const groupCartEntriesByOtherParty = rows => {
  return Object.values(
    rows.reduce((groups, row) => {
      const { cartEntry, listing } = row;
      const sellerId =
        cartEntry?.otherPartyId ||
        cartEntry?.sellerId ||
        listing?.author?.id?.uuid ||
        'unknown-user';
      return {
        ...groups,
        [sellerId]: [...(groups[sellerId] || []), row],
      };
    }, {})
  );
};

const formatLineTotal = (intl, cartEntry, listing, transaction, quantity) => {
  const lineTotal = getCartEntryLineTotal(cartEntry, listing, transaction);
  if (lineTotal) {
    return formatMoney(intl, lineTotal);
  }

  const price = getCartEntryPrice(cartEntry, listing, transaction);
  return price ? formatMoney(intl, new Money(price.amount * quantity, price.currency)) : null;
};

const isPositiveIntegerInput = value => /^[1-9]\d*$/.test(String(value).trim());

const getCheckoutValidationError = (rows, config) => {
  const rowWithError = (rows || [])
    .map(row => {
      const { cartEntry, listing, transaction } = row || {};
      const title = getCartEntryTitle(cartEntry, listing);
      const quantity = cartEntry?.quantity || 1;
      const stockLimit = getCartStockLimit(cartEntry, listing, config, transaction);
      const hasInfiniteStock = hasUnlimitedCartStock(cartEntry, listing, config);

      if (!listing || isUnavailable(cartEntry, listing, config, transaction)) {
        return {
          messageId: 'CartPage.checkoutItemUnavailable',
          values: { title },
        };
      }

      if (!hasInfiniteStock && stockLimit != null && quantity > stockLimit) {
        return {
          messageId: 'CartPage.checkoutQuantityUnavailable',
          values: { stock: stockLimit, title },
        };
      }

      return null;
    })
    .find(Boolean);

  return rowWithError || null;
};

const SellerCartItem = props => {
  const {
    acceptedOfferActionError,
    acceptedOfferActionInProgress,
    config,
    intl,
    listing,
    cartEntry,
    transaction,
    quantityClampWarning,
    onAcceptOfferWithPayment,
    onAcceptOfferWithoutPayment,
    onRemove,
    onQuantityChange,
  } = props;
  const title = getCartEntryTitle(cartEntry, listing);
  const description = getCartEntryDescription(cartEntry, listing);
  const slug = createSlug(title);
  const cartItemIsAcceptedOffer = isAcceptedOfferCartItem(cartEntry);
  const isBuyerRequest = isBuyerRequestListing(listing);
  const imageUrl = cartEntryImageUrl(cartEntry, listing);
  const hasInfiniteStock = hasUnlimitedCartStock(cartEntry, listing, config);
  const maxQuantity = getCartStockLimit(cartEntry, listing, config, transaction);
  const quantity = cartEntry?.quantity || 1;
  const [quantityInput, setQuantityInput] = useState(String(quantity));
  const [quantityValidation, setQuantityValidation] = useState(null);
  const unavailable = isUnavailable(cartEntry, listing, config, transaction);
  const quantityTooHigh = !hasInfiniteStock && maxQuantity != null && quantity > maxQuantity;
  const stockUnavailable = !hasInfiniteStock && maxQuantity != null && maxQuantity < 1;
  const itemUnavailable = unavailable || quantityTooHigh;
  const price = getCartEntryPrice(cartEntry, listing, transaction);
  const lineTotal = getCartEntryLineTotal(cartEntry, listing, transaction);
  const acceptedOfferDetails = cartItemIsAcceptedOffer
    ? getAcceptedOfferCartPriceDetails(cartEntry, listing, transaction)
    : null;
  const formattedPrice =
    cartItemIsAcceptedOffer && acceptedOfferDetails?.totalPrice
      ? formatOfferUnitPrice(
          intl,
          acceptedOfferDetails.totalPrice,
          quantity,
          acceptedOfferDetails.unitPrice || price
        )
      : price
      ? formatMoney(intl, price)
      : null;
  const formattedAcceptedOfferTotal = lineTotal ? formatMoney(intl, lineTotal) : null;
  const formattedLineTotal = formatLineTotal(intl, cartEntry, listing, transaction, quantity);
  const priceToBeQuoted = intl.formatMessage({ id: 'CartPage.priceToBeQuoted' });
  const priceLabel = formattedPrice
    ? cartItemIsAcceptedOffer
      ? `Unit price: ${formattedPrice}`
      : formattedPrice
    : priceToBeQuoted;
  const lineTotalLabel = formattedLineTotal || priceToBeQuoted;
  const acceptedOfferActionDisabled = !!acceptedOfferActionInProgress;
  const itemLinkProps = cartItemIsAcceptedOffer
    ? {
        name: cartEntry.transactionRole === 'provider' ? 'SaleDetailsPage' : 'OrderDetailsPage',
        params: { id: cartEntry.transactionId },
      }
    : listing?.id
    ? {
        name: 'ListingPage',
        params: { id: listing.id.uuid, slug },
      }
    : null;
  const itemTypeMessageId = cartItemIsAcceptedOffer
    ? 'CartPage.acceptedOffer'
    : isBuyerRequest
    ? 'CartPage.buyerRequest'
    : 'CartPage.listing';
  const quantityLabel = intl.formatMessage({ id: 'CartPage.quantityLabel' });
  const quantityControlDisabled = (unavailable && !quantityTooHigh) || stockUnavailable;
  const quantityDecreaseLabel = intl.formatMessage({ id: 'CartPage.quantityDecrease' });
  const quantityIncreaseLabel = intl.formatMessage({ id: 'CartPage.quantityIncrease' });

  useEffect(() => {
    setQuantityInput(String(quantity));
  }, [quantity]);

  const setAvailableQuantityValidation = () => {
    setQuantityValidation(
      maxQuantity != null && maxQuantity > 0
        ? intl.formatMessage({ id: 'CartPage.quantityCannotExceedStock' })
        : null
    );
  };

  const commitQuantity = nextValue => {
    const rawValue = String(nextValue).trim();

    if (!isPositiveIntegerInput(rawValue)) {
      setQuantityInput(String(quantity));
      setQuantityValidation(null);
      return;
    }

    const parsedQuantity = Number.parseInt(rawValue, 10);
    const hasMaxQuantity = maxQuantity != null && maxQuantity > 0;
    const nextQuantity = hasMaxQuantity ? Math.min(parsedQuantity, maxQuantity) : parsedQuantity;

    if (hasMaxQuantity && parsedQuantity > maxQuantity) {
      setAvailableQuantityValidation();
    } else {
      setQuantityValidation(null);
    }

    setQuantityInput(String(nextQuantity));
    if (nextQuantity !== quantity) {
      onQuantityChange(nextQuantity, maxQuantity);
    }
  };

  const decreaseQuantity = () => {
    const nextQuantity =
      maxQuantity != null && maxQuantity > 0 && quantity > maxQuantity
        ? maxQuantity
        : Math.max(1, quantity - 1);
    commitQuantity(nextQuantity);
  };

  const increaseQuantity = () => {
    if (maxQuantity != null && maxQuantity > 0 && quantity >= maxQuantity) {
      setAvailableQuantityValidation();
      return;
    }
    commitQuantity(quantity + 1);
  };

  const handleQuantityKeyDown = event => {
    if (['e', 'E', '+', '-', '.'].includes(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <article className={css.cartItem}>
      {itemLinkProps ? (
        <NamedLink className={css.itemImageLink} {...itemLinkProps}>
          {imageUrl ? (
            <img className={css.itemImage} src={imageUrl} alt={title} />
          ) : (
            <div className={css.noImage} aria-hidden="true" />
          )}
        </NamedLink>
      ) : (
        <div className={css.itemImageLink}>
          {imageUrl ? (
            <img className={css.itemImage} src={imageUrl} alt={title} />
          ) : (
            <div className={css.noImage} aria-hidden="true" />
          )}
        </div>
      )}
      <div className={css.itemDetails}>
        <span className={css.itemTypePill}>
          <FormattedMessage id={itemTypeMessageId} />
        </span>
        {itemLinkProps ? (
          <NamedLink className={css.itemTitle} {...itemLinkProps}>
            {title}
          </NamedLink>
        ) : (
          <span className={css.itemTitle}>{title}</span>
        )}
        {description ? <p className={css.itemDescription}>{description}</p> : null}
        <div className={css.itemMeta}>
          <span>{priceLabel}</span>
          {cartItemIsAcceptedOffer && formattedAcceptedOfferTotal ? (
            <span>{`Total: ${formattedAcceptedOfferTotal}`}</span>
          ) : null}
          {getDeliveryMethod(cartEntry, listing) ? (
            <span>
              <FormattedMessage id={`CartPage.delivery.${getDeliveryMethod(cartEntry, listing)}`} />
            </span>
          ) : null}
        </div>
        {itemUnavailable ? (
          <p className={css.itemWarning}>
            {quantityTooHigh ? (
              <FormattedMessage id="CartPage.quantityUnavailable" values={{ stock: maxQuantity }} />
            ) : (
              <FormattedMessage id="CartPage.itemUnavailable" />
            )}
          </p>
        ) : null}
        {quantityClampWarning ? (
          <p className={css.itemWarning}>
            <FormattedMessage
              id="CartPage.quantityAvailable"
              values={{ stock: quantityClampWarning }}
            />
          </p>
        ) : null}
        {cartItemIsAcceptedOffer ? (
          <div className={css.acceptedOfferActions}>
            <SecondaryButton
              className={css.acceptedOfferActionButton}
              type="button"
              disabled={itemUnavailable || !transaction || acceptedOfferActionDisabled}
              inProgress={acceptedOfferActionInProgress === 'no-payment'}
              onClick={onAcceptOfferWithoutPayment}
            >
              <FormattedMessage id="TransactionPage.default-negotiation.customer.transition-accept-offer-without-payment.actionButton" />
            </SecondaryButton>
            <PrimaryButton
              className={css.acceptedOfferActionButton}
              type="button"
              disabled={itemUnavailable || !transaction || acceptedOfferActionDisabled}
              onClick={onAcceptOfferWithPayment}
            >
              <FormattedMessage id="TransactionPage.default-negotiation.customer.transition-request-payment-to-accept-offer.actionButton" />
            </PrimaryButton>
          </div>
        ) : null}
        {cartItemIsAcceptedOffer && acceptedOfferActionError ? (
          <p className={css.itemWarning}>
            <FormattedMessage id="CartPage.acceptedOfferActionError" />
          </p>
        ) : null}
        <button className={css.removeButton} type="button" onClick={onRemove}>
          <FormattedMessage id="CartPage.removeItem" />
        </button>
      </div>
      <div className={css.itemPurchaseRow}>
        <div className={css.quantityArea}>
          <div className={css.quantityControls} aria-label={quantityLabel}>
            <button
              className={css.quantityButton}
              type="button"
              aria-label={quantityDecreaseLabel}
              onClick={decreaseQuantity}
              disabled={quantityControlDisabled || quantity <= 1}
            >
              -
            </button>
            <input
              className={css.quantityInput}
              type="number"
              inputMode="numeric"
              min="1"
              max={maxQuantity != null && maxQuantity > 0 ? maxQuantity : undefined}
              step="1"
              aria-label={quantityLabel}
              value={quantityInput}
              disabled={quantityControlDisabled}
              onKeyDown={handleQuantityKeyDown}
              onChange={event => commitQuantity(event.target.value)}
            />
            <button
              className={css.quantityButton}
              type="button"
              aria-label={quantityIncreaseLabel}
              onClick={increaseQuantity}
              disabled={
                quantityControlDisabled ||
                (maxQuantity != null && maxQuantity > 0 && quantity >= maxQuantity)
              }
            >
              +
            </button>
          </div>
          {quantityValidation ? (
            <p className={css.quantityValidation}>{quantityValidation}</p>
          ) : null}
        </div>
        <div className={css.itemTotal}>
          <span className={css.itemTotalLabel}>
            <FormattedMessage id="CartPage.lineTotal" />
          </span>
          <strong>{lineTotalLabel}</strong>
        </div>
      </div>
    </article>
  );
};

const SellerCard = props => {
  const {
    config,
    acceptedOfferActionError,
    acceptedOfferActionInProgress,
    group,
    intl,
    onAcceptOfferWithPayment,
    onAcceptOfferWithoutPayment,
    onRemove,
    onQuantityChange,
    onNoPaymentCheckout,
  } = props;
  const firstRow = group[0] || {};
  const sellerName = getOtherPartyDisplayName(firstRow.cartEntry, firstRow.listing);
  const hasAcceptedOfferItems = group.some(row => isAcceptedOfferCartItem(row.cartEntry));
  const hasUnavailableItems = group.some(row => {
    const { cartEntry, listing, transaction } = row;
    const maxQuantity = getCartStockLimit(cartEntry, listing, config, transaction);
    const hasInfiniteStock = hasUnlimitedCartStock(cartEntry, listing, config);
    return (
      isUnavailable(cartEntry, listing, config, transaction) ||
      (!hasInfiniteStock && maxQuantity != null && cartEntry?.quantity > maxQuantity)
    );
  });
  const availableItems = group.filter(
    row => !isUnavailable(row.cartEntry, row.listing, config, row.transaction)
  );
  const firstAcceptedOfferRow = availableItems.find(
    row => isAcceptedOfferCartItem(row.cartEntry) && row.transaction
  );
  const totalAmount = availableItems.reduce((sum, row) => {
    const lineTotal = getCartEntryLineTotal(row.cartEntry, row.listing, row.transaction);
    const price = getCartEntryPrice(row.cartEntry, row.listing, row.transaction);
    return sum + (lineTotal?.amount || (price?.amount || 0) * (row.cartEntry?.quantity || 1));
  }, 0);
  const firstPricedItem = availableItems.find(
    row =>
      getCartEntryLineTotal(row.cartEntry, row.listing, row.transaction) ||
      getCartEntryPrice(row.cartEntry, row.listing, row.transaction)
  );
  const currency =
    getCartEntryLineTotal(
      firstPricedItem?.cartEntry,
      firstPricedItem?.listing,
      firstPricedItem?.transaction
    )?.currency ||
    getCartEntryPrice(
      firstPricedItem?.cartEntry,
      firstPricedItem?.listing,
      firstPricedItem?.transaction
    )?.currency;
  const formattedSubtotal = currency ? formatMoney(intl, new Money(totalAmount, currency)) : null;
  const itemCount = group.reduce((sum, row) => sum + (row.cartEntry?.quantity || 1), 0);

  return (
    <section className={css.sellerCard}>
      <div className={css.sellerHeader}>
        <div>
          <H2 className={css.sellerTitle}>
            <FormattedMessage id="CartPage.otherPartyHeading" values={{ sellerName }} />
          </H2>
          <p className={css.sellerMeta}>
            <FormattedMessage id="CartPage.itemCount" values={{ count: itemCount }} />
          </p>
        </div>
        <div className={css.sellerSummary}>
          <span className={css.subtotalLabel}>
            <FormattedMessage id="CartPage.subtotal" />
          </span>
          <span className={css.subtotalValue}>
            {formattedSubtotal || <FormattedMessage id="CartPage.priceToBeQuoted" />}
          </span>
        </div>
      </div>
      <div className={css.items}>
        {group.map(row => {
          const { cartEntry, listing, transaction } = row;
          const cartItemKey = getCartItemKey(cartEntry);
          const acceptedOfferActionKey =
            acceptedOfferActionInProgress?.cartItemKey === cartItemKey
              ? acceptedOfferActionInProgress.action
              : null;
          return (
            <SellerCartItem
              key={cartItemKey}
              acceptedOfferActionError={acceptedOfferActionError === cartItemKey}
              acceptedOfferActionInProgress={acceptedOfferActionKey}
              config={config}
              intl={intl}
              listing={listing}
              cartEntry={cartEntry}
              transaction={transaction}
              quantityClampWarning={row.quantityClampWarning}
              onAcceptOfferWithPayment={() => onAcceptOfferWithPayment(row, group)}
              onAcceptOfferWithoutPayment={() => onAcceptOfferWithoutPayment(row)}
              onRemove={() => onRemove(cartItemKey)}
              onQuantityChange={(quantity, maxQuantity) =>
                onQuantityChange(cartItemKey, quantity, maxQuantity)
              }
            />
          );
        })}
      </div>
      {hasAcceptedOfferItems ? (
        <div className={css.checkoutRow}>
          {hasUnavailableItems ? (
            <p className={css.checkoutWarning}>
              <FormattedMessage id="CartPage.checkoutUnavailable" />
            </p>
          ) : null}
          <div className={css.checkoutActions}>
            <p className={css.checkoutHint}>
              Accept and no pay is available on each accepted offer only. It will not change the
              grouped pay checkout total.
            </p>
            <PrimaryButton
              className={css.checkoutButton}
              type="button"
              disabled={hasUnavailableItems || !firstAcceptedOfferRow}
              onClick={() => onAcceptOfferWithPayment(firstAcceptedOfferRow, group)}
            >
              <FormattedMessage id="CartPage.checkoutWithSeller" values={{ sellerName }} />
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <div className={css.checkoutRow}>
          {hasUnavailableItems ? (
            <p className={css.checkoutWarning}>
              <FormattedMessage id="CartPage.checkoutUnavailable" />
            </p>
          ) : null}
          <div className={css.checkoutActions}>
            <SecondaryButton
              className={css.checkoutButton}
              type="button"
              disabled={hasUnavailableItems || availableItems.length === 0}
              onClick={() => onNoPaymentCheckout(group)}
            >
              <FormattedMessage id="CartPage.sendRequestWithoutPayment" />
            </SecondaryButton>
          </div>
        </div>
      )}
    </section>
  );
};

const NoPaymentCheckoutModal = props => {
  const {
    group,
    intl,
    isOpen,
    note,
    error,
    inProgress,
    onChangeNote,
    onClose,
    onManageDisableScrolling,
    onSubmit,
  } = props;
  const firstRow = group?.[0] || {};
  const sellerName = getOtherPartyDisplayName(firstRow.cartEntry, firstRow.listing);
  const itemCount = (group || []).reduce((sum, row) => sum + (row.cartEntry?.quantity || 1), 0);
  const totalAmount = (group || []).reduce((sum, row) => {
    const lineTotal = getCartEntryLineTotal(row.cartEntry, row.listing, row.transaction);
    const price = getCartEntryPrice(row.cartEntry, row.listing, row.transaction);
    return sum + (lineTotal?.amount || (price?.amount || 0) * (row.cartEntry?.quantity || 1));
  }, 0);
  const currency =
    getCartEntryLineTotal(firstRow.cartEntry, firstRow.listing, firstRow.transaction)?.currency ||
    getCartEntryPrice(firstRow.cartEntry, firstRow.listing, firstRow.transaction)?.currency;
  const formattedTotal = currency ? formatMoney(intl, new Money(totalAmount, currency)) : null;

  return (
    <Modal
      id="CartPage.noPaymentCheckoutModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      containerClassName={css.noPaymentModalContainer}
      contentClassName={css.noPaymentModalContent}
      usePortal
    >
      <div className={css.noPaymentHeader}>
        <H2 className={css.noPaymentTitle}>
          <FormattedMessage id="CartPage.noPaymentModalTitle" values={{ sellerName }} />
        </H2>
        <p className={css.noPaymentCopy}>
          <FormattedMessage id="CartPage.noPaymentModalDescription" />
        </p>
      </div>

      <div className={css.noPaymentBody}>
        <div className={css.noPaymentSummary}>
          <div className={css.noPaymentSummaryHeader}>
            <span>
              <FormattedMessage id="CartPage.noPaymentItemsTitle" values={{ count: itemCount }} />
            </span>
            {formattedTotal ? (
              <strong>
                <FormattedMessage
                  id="CartPage.noPaymentSummaryTotal"
                  values={{ total: formattedTotal }}
                />
              </strong>
            ) : null}
          </div>
          {(group || []).map(row => {
            const { cartEntry, listing } = row;
            const quantity = cartEntry?.quantity || 1;
            const price = getCartEntryPrice(cartEntry, listing, row.transaction);
            const lineTotal = getCartEntryLineTotal(cartEntry, listing, row.transaction);
            return (
              <div key={getCartItemKey(cartEntry)} className={css.noPaymentSummaryItem}>
                <span>{getCartEntryTitle(cartEntry, listing)}</span>
                <span>
                  <FormattedMessage
                    id="CartPage.noPaymentItemMeta"
                    values={{
                      quantity,
                      price: price
                        ? formatMoney(intl, price)
                        : intl.formatMessage({ id: 'CartPage.priceToBeQuoted' }),
                    }}
                  />
                </span>
                {lineTotal ? <span>{`Total: ${formatMoney(intl, lineTotal)}`}</span> : null}
              </div>
            );
          })}
        </div>

        <label className={css.noteLabel} htmlFor="CartPage.noPaymentNote">
          <FormattedMessage id="CartPage.noPaymentNoteLabel" />
        </label>
        <textarea
          id="CartPage.noPaymentNote"
          className={css.noteTextarea}
          value={note}
          maxLength={2000}
          onChange={e => onChangeNote(e.target.value)}
          placeholder={intl.formatMessage({ id: 'CartPage.noPaymentNotePlaceholder' })}
        />

        {error ? (
          <p className={css.errorText}>
            {error?.messageId ? (
              <FormattedMessage id={error.messageId} values={error.values || {}} />
            ) : (
              <FormattedMessage id="CartPage.noPaymentError" />
            )}
          </p>
        ) : null}

        <div className={css.noPaymentActions}>
          <SecondaryButton
            className={css.noPaymentActionButton}
            type="button"
            onClick={onClose}
            disabled={inProgress}
          >
            <FormattedMessage id="CartPage.noPaymentCancel" />
          </SecondaryButton>
          <PrimaryButton
            className={css.noPaymentActionButton}
            type="button"
            onClick={onSubmit}
            inProgress={inProgress}
            disabled={inProgress}
          >
            <FormattedMessage id="CartPage.noPaymentSubmit" />
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export const CartPageComponent = props => {
  const {
    intl,
    acceptedOfferTransactions,
    listings,
    searchInProgress,
    searchListingsError,
    scrollingDisabled,
    callSetInitialValues,
    onAcceptOfferWithoutPayment,
    onFetchCartListings,
    onInitializeCardPaymentData,
    onManageDisableScrolling,
  } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const [cartItems, setCartItems] = useState([]);
  const [cartReady, setCartReady] = useState(false);
  const [noPaymentGroup, setNoPaymentGroup] = useState(null);
  const [noPaymentNote, setNoPaymentNote] = useState('');
  const [noPaymentInProgress, setNoPaymentInProgress] = useState(false);
  const [noPaymentError, setNoPaymentError] = useState(null);
  const [noPaymentSuccessSellerName, setNoPaymentSuccessSellerName] = useState(null);
  const [acceptedOfferActionInProgress, setAcceptedOfferActionInProgress] = useState(null);
  const [acceptedOfferActionError, setAcceptedOfferActionError] = useState(null);
  const [checkoutValidationError, setCheckoutValidationError] = useState(null);
  const [quantityClampWarnings, setQuantityClampWarnings] = useState({});

  const refreshCart = () => {
    setCartItems(readCartItems());
    setCartReady(true);
    onFetchCartListings();
  };

  useEffect(() => {
    refreshCart();
    const handleCartUpdated = () => setCartItems(readCartItems());
    window.addEventListener(CART_UPDATED_EVENT, handleCartUpdated);
    return () => window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdated);
  }, []);

  const listingIds = Array.from(new Set(cartItems.map(item => item.listingId)));
  const listingsInCart = listings.filter(listing => listingIds.includes(listing.id.uuid));
  const listingsById = new Map(listingsInCart.map(listing => [listing.id.uuid, listing]));
  const transactionsById = new Map(
    (acceptedOfferTransactions || []).map(transaction => [transaction.id.uuid, transaction])
  );
  const cartRows = cartItems.map(cartEntry => {
    const cartItemKey = getCartItemKey(cartEntry);
    return {
      cartEntry,
      listing: listingsById.get(cartEntry.listingId),
      transaction: cartEntry.transactionId ? transactionsById.get(cartEntry.transactionId) : null,
      quantityClampWarning: quantityClampWarnings[cartItemKey],
    };
  });
  const renderableCartRows = cartRows.filter(row => !!row.listing);
  const unavailableCartIds = cartRows
    .filter(row => !row.listing)
    .map(row => getCartItemKey(row.cartEntry));
  const sellerGroups = useMemo(() => groupCartEntriesByOtherParty(renderableCartRows), [
    renderableCartRows,
  ]);

  useEffect(() => {
    if (!cartReady || searchInProgress || renderableCartRows.length === 0) {
      return;
    }

    const rowsByCartItemKey = new Map(
      renderableCartRows.map(row => [getCartItemKey(row.cartEntry), row])
    );
    let didClampQuantity = false;
    const nextClampWarnings = {};
    const nextCartItems = cartItems.map(item => {
      const cartItemKey = getCartItemKey(item);
      const row = rowsByCartItemKey.get(cartItemKey);
      if (!row) {
        return item;
      }

      const maxQuantity = getCartStockLimit(row.cartEntry, row.listing, config, row.transaction);
      const quantity = row.cartEntry?.quantity || 1;
      if (maxQuantity != null && maxQuantity > 0 && quantity > maxQuantity) {
        didClampQuantity = true;
        nextClampWarnings[cartItemKey] = maxQuantity;
        return { ...item, quantity: maxQuantity };
      }

      return item;
    });

    if (didClampQuantity) {
      const nextItems = writeCartItems(nextCartItems);
      setCartItems(nextItems);
      setQuantityClampWarnings(previousWarnings => ({
        ...previousWarnings,
        ...nextClampWarnings,
      }));
      setCheckoutValidationError(null);
    }
  }, [cartReady, searchInProgress, cartItems, renderableCartRows, config]);

  const handleRemove = listingId => {
    const nextItems = removeCartItem(listingId);
    setCartItems(nextItems);
    setCheckoutValidationError(null);
    setQuantityClampWarnings(previousWarnings => {
      const nextWarnings = { ...previousWarnings };
      delete nextWarnings[listingId];
      return nextWarnings;
    });
  };

  const handleRemoveUnavailable = () => {
    const nextItems = removeCartItems(unavailableCartIds);
    setCartItems(nextItems);
    setCheckoutValidationError(null);
    setQuantityClampWarnings(previousWarnings => {
      const nextWarnings = { ...previousWarnings };
      unavailableCartIds.forEach(id => {
        delete nextWarnings[id];
      });
      return nextWarnings;
    });
  };

  const handleQuantityChange = (cartItemId, quantity, maxQuantity) => {
    const nextItems = updateCartItemQuantity(cartItemId, quantity, { maxQuantity });
    setCartItems(nextItems);
    setCheckoutValidationError(null);
    setNoPaymentError(null);
    setQuantityClampWarnings(previousWarnings => {
      const nextWarnings = { ...previousWarnings };
      delete nextWarnings[cartItemId];
      return nextWarnings;
    });
  };

  const validateRowsForCheckout = rows => {
    const validationError = getCheckoutValidationError(rows, config);
    setCheckoutValidationError(validationError);
    return !validationError;
  };

  const handleAcceptedOfferPaymentCheckout = (row, groupRows) => {
    const checkoutRows = Array.isArray(groupRows) && groupRows.length > 0 ? groupRows : [row];
    const anchorRow =
      row?.transaction && isAcceptedOfferCartItem(row.cartEntry)
        ? row
        : checkoutRows.find(
            checkoutRow =>
              checkoutRow?.transaction && isAcceptedOfferCartItem(checkoutRow.cartEntry)
          );
    const { listing, transaction } = anchorRow || {};
    if (!listing || !transaction) {
      return;
    }
    if (!validateRowsForCheckout(checkoutRows)) {
      return;
    }

    const checkoutCartItems = checkoutRows.map(checkoutRow =>
      toCheckoutCartItemWithMetadata(
        checkoutRow.listing,
        checkoutRow.cartEntry,
        config,
        checkoutRow.transaction
      )
    );
    logCartCheckoutHandoff({
      checkoutItems: checkoutCartItems,
      anchorTransactionId: transaction.id.uuid,
    });

    const initialValues = {
      listing,
      transaction,
      orderData: {
        cartItems: checkoutCartItems,
      },
      confirmPaymentError: null,
    };
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routeConfiguration);

    callSetInitialValues(setInitialValues, initialValues);
    onInitializeCardPaymentData();
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routeConfiguration,
        { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
        {}
      )
    );
  };

  const handleAcceptOfferWithoutPayment = (row, groupRows) => {
    const checkoutRows = Array.isArray(groupRows) && groupRows.length > 0 ? groupRows : [row];
    const anchorRow =
      row?.transaction && isAcceptedOfferCartItem(row.cartEntry)
        ? row
        : checkoutRows.find(
            checkoutRow =>
              checkoutRow?.transaction && isAcceptedOfferCartItem(checkoutRow.cartEntry)
          );
    const { cartEntry, listing, transaction } = anchorRow || {};
    const cartItemKey = getCartItemKey(cartEntry);
    if (!cartItemKey || !listing || !transaction || acceptedOfferActionInProgress) {
      return;
    }
    if (!validateRowsForCheckout(checkoutRows)) {
      return;
    }

    const checkoutCartItems = checkoutRows.map(checkoutRow =>
      toCheckoutCartItemWithMetadata(
        checkoutRow.listing,
        checkoutRow.cartEntry,
        config,
        checkoutRow.transaction
      )
    );
    const transitionParams = {
      orderData: {
        cartItems: checkoutCartItems,
      },
      protectedData: cartProtectedDataForCheckoutItems(checkoutCartItems),
    };
    logCartCheckoutHandoff({
      checkoutItems: checkoutCartItems,
      anchorTransactionId: transaction.id.uuid,
    });

    setAcceptedOfferActionInProgress({ cartItemKey, action: 'no-payment' });
    setAcceptedOfferActionError(null);

    onAcceptOfferWithoutPayment(
      transaction.id,
      ACCEPT_OFFER_WITHOUT_PAYMENT_TRANSITION,
      transitionParams
    )
      .then(() => {
        const nextItems = removeCartItem(cartItemKey);
        setCartItems(nextItems);
        onFetchCartListings();
      })
      .catch(() => {
        setAcceptedOfferActionError(cartItemKey);
      })
      .finally(() => {
        setAcceptedOfferActionInProgress(null);
      });
  };

  const handleOpenNoPaymentCheckout = group => {
    if (!validateRowsForCheckout(group)) {
      return;
    }

    setNoPaymentGroup(group);
    setNoPaymentNote('');
    setNoPaymentError(null);
    setNoPaymentSuccessSellerName(null);
  };

  const handleCloseNoPaymentCheckout = () => {
    if (!noPaymentInProgress) {
      setNoPaymentGroup(null);
      setNoPaymentNote('');
      setNoPaymentError(null);
    }
  };

  const handleSubmitNoPaymentCheckout = () => {
    if (!noPaymentGroup?.length || noPaymentInProgress) {
      return;
    }
    const validationError = getCheckoutValidationError(noPaymentGroup, config);
    if (validationError) {
      setCheckoutValidationError(validationError);
      setNoPaymentError(validationError);
      return;
    }

    const checkoutItems = noPaymentGroup.map(row =>
      toCartCheckoutItem(row.listing, row.cartEntry, config, row.transaction)
    );
    const listingIds = checkoutItems.map(item => item.listingId);
    const cartItemIds = noPaymentGroup.map(row => getCartItemKey(row.cartEntry));
    const sellerName = getOtherPartyDisplayName(
      noPaymentGroup[0]?.cartEntry,
      noPaymentGroup[0]?.listing
    );

    setNoPaymentInProgress(true);
    setNoPaymentError(null);

    cartNoPaymentCheckout({
      listingIds,
      items: checkoutItems.map(item => ({
        listingId: item.listingId,
        quantity: item.stockCount,
        deliveryMethod: item.deliveryMethod,
        isInfiniteStock: item.isInfiniteStock,
        selectedOptions: item.selectedOptions,
      })),
      note: noPaymentNote,
    })
      .then(() => {
        const nextItems = removeCartItems(cartItemIds);
        setCartItems(nextItems);
        setNoPaymentSuccessSellerName(sellerName);
        setNoPaymentGroup(null);
        setNoPaymentNote('');
        onFetchCartListings();
      })
      .catch(e => {
        setNoPaymentError(e);
      })
      .finally(() => {
        setNoPaymentInProgress(false);
      });
  };

  const hasCartItems = cartItems.length > 0;
  const hasRenderableItems = sellerGroups.length > 0;
  const showEmptyState = cartReady && !searchInProgress && !hasCartItems;

  return (
    <Page
      title={intl.formatMessage({ id: 'CartPage.schemaTitle' })}
      description={intl.formatMessage({ id: 'CartPage.schemaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <main className={css.content}>
          <div className={css.header}>
            <div>
              <H1 className={css.title}>
                <FormattedMessage id="CartPage.title" />
              </H1>
              <p className={css.subtitle}>
                <FormattedMessage id="CartPage.subtitle" />
              </p>
            </div>
            <NamedLink name="SearchPage" className={css.continueLink}>
              <FormattedMessage id="CartPage.continueShopping" />
            </NamedLink>
          </div>

          {searchInProgress ? (
            <p className={css.statusText}>
              <FormattedMessage id="CartPage.loading" />
            </p>
          ) : null}
          {searchListingsError ? (
            <p className={css.errorText}>
              <FormattedMessage id="CartPage.fetchError" />
            </p>
          ) : null}
          {noPaymentSuccessSellerName ? (
            <p className={css.successText}>
              <FormattedMessage
                id="CartPage.noPaymentSuccess"
                values={{ sellerName: noPaymentSuccessSellerName }}
              />
            </p>
          ) : null}
          {checkoutValidationError ? (
            <p className={css.errorText}>
              <FormattedMessage
                id={checkoutValidationError.messageId}
                values={checkoutValidationError.values || {}}
              />
            </p>
          ) : null}

          {unavailableCartIds.length > 0 ? (
            <section className={css.unavailableCard}>
              <div>
                <H2 className={css.unavailableTitle}>
                  <FormattedMessage id="CartPage.unavailableTitle" />
                </H2>
                <p className={css.unavailableCopy}>
                  <FormattedMessage
                    id="CartPage.unavailableDescription"
                    values={{ count: unavailableCartIds.length }}
                  />
                </p>
              </div>
              <Button type="button" onClick={handleRemoveUnavailable}>
                <FormattedMessage id="CartPage.removeUnavailable" />
              </Button>
            </section>
          ) : null}

          {showEmptyState ? (
            <section className={css.emptyState}>
              <div className={css.emptyVisual} aria-hidden="true">
                <span className={css.emptyCartGlyph} />
              </div>
              <div className={css.emptyContent}>
                <span className={css.emptyKicker}>
                  <FormattedMessage id="CartPage.emptyKicker" />
                </span>
                <H2 className={css.emptyTitle}>
                  <FormattedMessage id="CartPage.emptyTitle" />
                </H2>
                <p className={css.emptyCopy}>
                  <FormattedMessage id="CartPage.emptyDescription" />
                </p>
                <div className={css.emptyActions}>
                  <NamedLink name="SearchPage" className={css.emptyPrimaryLink}>
                    <FormattedMessage id="CartPage.emptyBrowseListings" />
                  </NamedLink>
                  <NamedLink
                    name="InboxPage"
                    params={{ tab: 'orders' }}
                    className={css.emptySecondaryLink}
                  >
                    <FormattedMessage id="CartPage.emptyOpenInbox" />
                  </NamedLink>
                </div>
              </div>
            </section>
          ) : hasRenderableItems ? (
            <div className={css.sellerCards}>
              {sellerGroups.map(group => (
                <SellerCard
                  key={
                    group[0]?.cartEntry?.otherPartyId ||
                    group[0]?.cartEntry?.sellerId ||
                    group[0]?.listing?.author?.id?.uuid ||
                    getCartItemKey(group[0]?.cartEntry)
                  }
                  config={config}
                  acceptedOfferActionError={acceptedOfferActionError}
                  acceptedOfferActionInProgress={acceptedOfferActionInProgress}
                  group={group}
                  intl={intl}
                  onAcceptOfferWithPayment={handleAcceptedOfferPaymentCheckout}
                  onAcceptOfferWithoutPayment={handleAcceptOfferWithoutPayment}
                  onRemove={handleRemove}
                  onQuantityChange={handleQuantityChange}
                  onNoPaymentCheckout={handleOpenNoPaymentCheckout}
                />
              ))}
            </div>
          ) : null}
        </main>
        <NoPaymentCheckoutModal
          group={noPaymentGroup}
          intl={intl}
          isOpen={!!noPaymentGroup}
          note={noPaymentNote}
          error={noPaymentError}
          inProgress={noPaymentInProgress}
          onChangeNote={setNoPaymentNote}
          onClose={handleCloseNoPaymentCheckout}
          onManageDisableScrolling={onManageDisableScrolling}
          onSubmit={handleSubmitNoPaymentCheckout}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { cartItems, currentPageResultIds, searchInProgress, searchListingsError } = state.CartPage;
  const listings = getListingsById(state, currentPageResultIds);
  const acceptedOfferTransactionRefs = (cartItems || [])
    .filter(isAcceptedOfferCartItem)
    .filter(item => item.transactionId)
    .map(item => ({ id: new sdkTypes.UUID(item.transactionId), type: 'transaction' }));
  const acceptedOfferTransactions = getMarketplaceEntities(state, acceptedOfferTransactionRefs);
  return {
    acceptedOfferTransactions,
    listings,
    searchInProgress,
    searchListingsError,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  callSetInitialValues: (setInitialValues, values) => dispatch(setInitialValues(values)),
  onAcceptOfferWithoutPayment: (txId, transitionName, params = {}) =>
    dispatch(makeTransitionThunk({ txId, transitionName, params })).unwrap(),
  onFetchCartListings: () => dispatch(searchCartListings()),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const CartPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(CartPageComponent);

export default CartPage;
